import { useEffect, useRef } from 'react';

export interface LastFactFindField {
    page: string;
    last_interacted_field?: string;
}

export const getFactFindPageName = (pathname: string) =>
    pathname.replace(/\/$/, '').replace(/^\/eligibility\//, '');

const getNamedElement = (target: EventTarget | null): Element | null => {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest(
        'input[name], select[name], textarea[name], button[name], [name]',
    );
};

const getFieldName = (target: EventTarget | null) =>
    getNamedElement(target)?.getAttribute('name') || '';

export const useLastFactFindField = (
    pathname: string,
    isTrackingEnabled = true,
) => {
    const pageRef = useRef(getFactFindPageName(pathname));
    const isTrackingEnabledRef = useRef(isTrackingEnabled);
    const lastFieldRef = useRef<LastFactFindField>({
        page: pageRef.current,
        last_interacted_field: undefined,
    });

    useEffect(() => {
        pageRef.current = getFactFindPageName(pathname);
        lastFieldRef.current.page = pageRef.current;
    }, [pathname]);

    useEffect(() => {
        isTrackingEnabledRef.current = isTrackingEnabled;
    }, [isTrackingEnabled]);

    useEffect(() => {
        const handleInteraction = (event: Event) => {
            if (!isTrackingEnabledRef.current) {
                return;
            }

            const fieldName = getFieldName(event.target);

            if (!fieldName) {
                return;
            }

            lastFieldRef.current = {
                page: pageRef.current,
                last_interacted_field: fieldName,
            };
        };

        document.addEventListener('focusin', handleInteraction, true);
        document.addEventListener('input', handleInteraction, true);
        document.addEventListener('change', handleInteraction, true);
        document.addEventListener('click', handleInteraction, true);

        return () => {
            document.removeEventListener('focusin', handleInteraction, true);
            document.removeEventListener('input', handleInteraction, true);
            document.removeEventListener('change', handleInteraction, true);
            document.removeEventListener('click', handleInteraction, true);
        };
    }, []);

    return lastFieldRef;
};
